import * as React from "react";
import { CalendarIcon, XIcon } from "lucide-react";
import {
  // startOfWeek,
  // endOfWeek,
  subDays,
  startOfMonth,
  endOfMonth,
  // startOfYear,
  // endOfYear,
  startOfDay,
  endOfDay,
  subYears,
} from "date-fns";
import { toDate, formatInTimeZone } from "date-fns-tz";
import { cva } from "class-variance-authority";

import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
// import { useUserCountry } from "./useUserCountry";

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const multiSelectVariants = cva(
  "flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium text-foreground ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground text-background",
        link: "text-primary underline-offset-4 hover:underline text-background",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export const CalendarDatePicker = React.forwardRef(
  (
    {
      id = "calendar-date-picker",
      className,
      date: selectedDate,
      closeOnSelect = true,
      numberOfMonths = 2,
      yearsRange = 30,
      onDateSelect,
      variant,
      align,
      disabledDate = "",
      ddmmyyyyFormat = false,
      onClear = false,
      ...props
    },
    ref
  ) => {
    const [date, setDate] = React.useState(selectedDate);
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [selectedRange, setSelectedRange] = React.useState(
      numberOfMonths === 2 ? "This Year" : "Today"
    );
    const [monthFrom, setMonthFrom] = React.useState(date?.from);
    const [yearFrom, setYearFrom] = React.useState(date?.from?.getFullYear());
    const [monthTo, setMonthTo] = React.useState(
      numberOfMonths === 2 ? date?.to : date?.from
    );
    const [yearTo, setYearTo] = React.useState(
      numberOfMonths === 2 ? date?.to?.getFullYear() : date?.from?.getFullYear()
    );
    const [highlightedPart, setHighlightedPart] = React.useState(null);
    const [clickCount, setClickCount] = React.useState(0);

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const handleClose = () => setIsPopoverOpen(false);

    const handleTogglePopover = () => setIsPopoverOpen((prev) => !prev);

    const selectDateRange = (from, to, range) => {
      const startDate = startOfDay(toDate(from, { timeZone }));
      const endDate =
        numberOfMonths === 2 ? endOfDay(toDate(to, { timeZone })) : startDate;
      onDateSelect({ from: startDate, to: endDate });
      setDate({ from: startDate, to: endDate });
      setSelectedRange(range);
      setMonthFrom(from);
      setYearFrom(from.getFullYear());
      setMonthTo(to);
      setYearTo(to.getFullYear());
      closeOnSelect && setIsPopoverOpen(false);
    };

    // const handleDateSelect = (range) => {
    //   if (range) {
    //     let from = startOfDay(toDate(range.from, { timeZone }));
    //     let to = range.to ? endOfDay(toDate(range.to, { timeZone })) : from;
    //     if (numberOfMonths === 1) {
    //       if (range.from !== date.from) {
    //         to = from;
    //       } else {
    //         from = startOfDay(toDate(range.to, { timeZone }));
    //       }
    //     }
    //     onDateSelect({ from, to });
    //     setMonthFrom(from);
    //     setYearFrom(from.getFullYear());
    //     setMonthTo(to);
    //     setYearTo(to.getFullYear());
    //     closeOnSelect && setIsPopoverOpen(false);
    //   }
    //   setSelectedRange(null);
    // };

    const handleDateSelect = (range) => {
      if (clickCount === 1 && date.to === undefined && range === undefined) {
        range = { from: date?.from, to: date?.from };
      }
      if (!range) return;
      let from = startOfDay(toDate(range.from, { timeZone }));
      let to = range.to ? endOfDay(toDate(range.to, { timeZone })) : from;
      if (clickCount === 0) {
        // First click → set only from

        if (range?.from >= date?.from) {
          if (to > from) {
            [from, to] = [to, from];
          }
        }

        // onDateSelect({ from, to: undefined });
        setClickCount(1); // wait for second click
        setDate({ from, to: undefined });
        setMonthFrom(from);
        setYearFrom(from.getFullYear());
        setMonthTo(undefined);
        setYearTo(undefined);
      } else {
        // Second click → finalize range
        if (numberOfMonths === 1) {
          if (range.from !== date?.from) {
            to = from; // same-day selection if clicked different start
          } else {
            from = startOfDay(toDate(range.to, { timeZone }));
          }
        }
        // Ensure from <= to
        if (!to) to = from;
        if (to <= from) {
          [from, to] = [to, from];
        }
        setDate({ from, to });
        onDateSelect({ from, to });
        setMonthFrom(from);
        setYearFrom(from.getFullYear());
        setMonthTo(to);
        setYearTo(to.getFullYear());
        closeOnSelect && setIsPopoverOpen(false);
        setClickCount(0); // reset for next range
      }

      setSelectedRange(null); // clear quick range highlight
    };

    const handleMonthChange = (newMonthIndex, part) => {
      setSelectedRange(null);
      if (part === "from") {
        if (yearFrom !== undefined) {
          if (newMonthIndex < 0 || newMonthIndex > yearsRange + 1) return;
          const newMonth = new Date(yearFrom, newMonthIndex, 1);
          const from =
            numberOfMonths === 2
              ? startOfMonth(toDate(newMonth, { timeZone }))
              : date?.from
              ? new Date(
                  date.from.getFullYear(),
                  newMonth.getMonth(),
                  date.from.getDate()
                )
              : newMonth;
          const to =
            numberOfMonths === 2
              ? date.to
                ? endOfDay(toDate(date.to, { timeZone }))
                : endOfMonth(toDate(newMonth, { timeZone }))
              : from;
          if (from <= to) {
            onDateSelect({ from, to });
            setDate({ from, to });
            setMonthFrom(newMonth);
            setMonthTo(date.to);
          }
        }
      } else {
        if (yearTo !== undefined) {
          if (newMonthIndex < 0 || newMonthIndex > yearsRange + 1) return;
          const newMonth = new Date(yearTo, newMonthIndex, 1);
          const from = date.from
            ? startOfDay(toDate(date.from, { timeZone }))
            : startOfMonth(toDate(newMonth, { timeZone }));
          const to =
            numberOfMonths === 2
              ? endOfMonth(toDate(newMonth, { timeZone }))
              : from;
          if (from <= to) {
            onDateSelect({ from, to });
            setDate({ from, to });
            setMonthTo(newMonth);
            setMonthFrom(date.from);
          }
        }
      }
    };

    const handleYearChange = (newYear, part) => {
      setSelectedRange(null);
      if (part === "from") {
        if (years.includes(newYear)) {
          const newMonth = monthFrom
            ? new Date(newYear, monthFrom ? monthFrom.getMonth() : 0, 1)
            : new Date(newYear, 0, 1);
          const from =
            numberOfMonths === 2
              ? startOfMonth(toDate(newMonth, { timeZone }))
              : date.from
              ? new Date(newYear, newMonth.getMonth(), date.from.getDate())
              : newMonth;
          const to =
            numberOfMonths === 2
              ? date.to
                ? endOfDay(toDate(date.to, { timeZone }))
                : endOfMonth(toDate(newMonth, { timeZone }))
              : from;
          if (from <= to) {
            onDateSelect({ from, to });
            setDate({ from, to });
            setYearFrom(newYear);
            setMonthFrom(newMonth);
            setYearTo(date.to?.getFullYear());
            setMonthTo(date.to);
          }
        }
      } else {
        if (years.includes(newYear)) {
          const newMonth = monthTo
            ? new Date(newYear, monthTo.getMonth(), 1)
            : new Date(newYear, 0, 1);
          const from = date.from
            ? startOfDay(toDate(date.from, { timeZone }))
            : startOfMonth(toDate(newMonth, { timeZone }));
          const to =
            numberOfMonths === 2
              ? endOfMonth(toDate(newMonth, { timeZone }))
              : from;
          if (from <= to) {
            onDateSelect({ from, to });
            setDate({ from, to });
            setYearTo(newYear);
            setMonthTo(newMonth);
            setYearFrom(date.from?.getFullYear());
            setMonthFrom(date.from);
          }
        }
      }
    };

    const today = new Date();

    const years = Array.from(
      { length: yearsRange + 1 },
      (_, i) => today.getFullYear() - yearsRange / 2 + i
    );

    // 🌍 Detect user's country using Intl API
    // const getUserCountry = () => {
    //   // const locale = Intl.DateTimeFormat().resolvedOptions().locale;
    //   // return locale.split("-")[1] || "AU"; //Dynamic Country detect
    //   return "AU";
    // };

    // const detectedCountry =  useUserCountry();
    const detectedCountry = "AU";

    // 📅 Country-wise Financial Year Configuration
    const financialYearConfig = {
      IN: { startMonth: 3, startDay: 1 }, // India: April 1 - March 31
      US: { startMonth: 9, startDay: 1 }, // USA: Oct 1 - Sep 30 (Federal)
      UK: { startMonth: 3, startDay: 6 }, // UK: April 6 - April 5
      AU: { startMonth: 6, startDay: 1 }, // Australia: July 1 - June 30
      CA: { startMonth: 3, startDay: 1 }, // Canada: April 1 - March 31
      JP: { startMonth: 3, startDay: 1 }, // Japan: April 1 - March 31
      CN: { startMonth: 0, startDay: 1 }, // China: Jan 1 - Dec 31
      DE: { startMonth: 0, startDay: 1 }, // Germany: Jan 1 - Dec 31
      FR: { startMonth: 0, startDay: 1 }, // France: Jan 1 - Dec 31
      AE: { startMonth: 0, startDay: 1 }, // UAE: Jan 1 - Dec 31
      ZA: { startMonth: 2, startDay: 1 }, // South Africa: March 1 - Feb 28
      NZ: { startMonth: 3, startDay: 1 }, // New Zealand: April 1 - March 31
    };

    const getEndOfMonth = (year, month) => {
      return new Date(year, month + 1, 0).getDate(); // Gets the last day of the month
    };

    // 📆 Function to Get Financial Year Range
    const getFinancialYearRange = (date, countryCode) => {
      const config =
        financialYearConfig[countryCode] || financialYearConfig["AU"]; // Default to India
      const { startMonth, startDay } = config;

      const year =
        date.getMonth() >= startMonth
          ? date.getFullYear()
          : date.getFullYear() - 1;

      const endMonth = (startMonth + 11) % 12; // Financial year runs for 12 months
      const endYear = startMonth === 0 ? year : year + 1; // Adjust for calendar year cases

      const lastDay = getEndOfMonth(endYear, endMonth); // Get the correct last day of the end month

      return {
        start: new Date(year, startMonth, startDay),
        end: new Date(endYear, endMonth, lastDay),
      };
    };

    const thisFY = getFinancialYearRange(today, detectedCountry);
    const lastFY = getFinancialYearRange(subYears(today, 1), detectedCountry);

    // 🌍 Country-wise Financial Year Start Month
    const financialYearQuaterConfig = {
      IN: 3, // India: April 1 - March 31
      US: 9, // USA: Oct 1 - Sep 30 (Federal)
      UK: 3, // UK: April 6 - April 5
      AU: 6, // Australia: July 1 - June 30
      CA: 3, // Canada: April 1 - March 31
      JP: 3, // Japan: April 1 - March 31
      CN: 0, // China: Jan 1 - Dec 31
      DE: 0, // Germany: Jan 1 - Dec 31
      FR: 0, // France: Jan 1 - Dec 31
      AE: 0, // UAE: Jan 1 - Dec 31
      ZA: 2, // South Africa: March 1 - Feb 28
      NZ: 3, // New Zealand: April 1 - March 31
    };

    const getQuarterDetails = (date, startMonth) => {
      // Get the current month and year
      const currentMonth = date.getMonth();
      const currentYear = date.getFullYear();

      // Adjust for financial year
      const financialYearStart =
        currentMonth >= startMonth ? currentYear : currentYear - 1;

      // Calculate the quarter index (0 = Q1, 1 = Q2, 2 = Q3, 3 = Q4)
      const quarterIndex = Math.floor(
        ((currentMonth - startMonth + 12) % 12) / 3
      );

      // 🔹 Compute Current Quarter Start & End Dates
      const quarterStartMonth = (startMonth + quarterIndex * 3) % 12;
      const quarterStartYear =
        quarterStartMonth >= startMonth
          ? financialYearStart
          : financialYearStart + 1;
      const quarterEndMonth = (quarterStartMonth + 2) % 12;
      const quarterEndYear =
        quarterEndMonth < quarterStartMonth
          ? quarterStartYear + 1
          : quarterStartYear;
      const quarterLastDay = new Date(
        quarterEndYear,
        quarterEndMonth + 1,
        0
      ).getDate();

      const thisQuarter = {
        start: new Date(quarterStartYear, quarterStartMonth, 1),
        end: new Date(quarterEndYear, quarterEndMonth, quarterLastDay),
      };

      // 🔹 Compute Last Quarter
      const lastQuarterEnd = new Date(thisQuarter.start);
      lastQuarterEnd.setDate(lastQuarterEnd.getDate() - 1);
      const lastQuarterStart = new Date(lastQuarterEnd);
      lastQuarterStart.setMonth(lastQuarterStart.getMonth() - 2);
      lastQuarterStart.setDate(1);

      return {
        thisQuarter,
        lastQuarter: { start: lastQuarterStart, end: lastQuarterEnd },
      };
    };

    // const quaterStartMonth =
    //   financialYearQuaterConfig[detectedCountry] ||
    //   financialYearQuaterConfig["AU"]; // Default to AU

    const quaterStartMonth = financialYearQuaterConfig["AU"]; // Default to AU
    const { thisQuarter, lastQuarter } = getQuarterDetails(
      today,
      quaterStartMonth
    );

    const dateRanges = [
      { label: "Today", start: today, end: today },
      { label: "Yesterday", start: subDays(today, 1), end: subDays(today, 1) },
      // {
      //   label: "This Week",
      //   start: startOfWeek(today, { weekStartsOn: 1 }),
      //   end: endOfWeek(today, { weekStartsOn: 1 }),
      // },
      // {
      //   label: "Last Week",
      //   start: subDays(startOfWeek(today, { weekStartsOn: 1 }), 7),
      //   end: subDays(endOfWeek(today, { weekStartsOn: 1 }), 7),
      // },
      // { label: "Last 7 Days", start: subDays(today, 6), end: today },
      {
        label: "This Month",
        start: startOfMonth(today),
        end: endOfMonth(today),
      },
      {
        label: "Last Month",
        start: startOfMonth(subDays(today, today.getDate())),
        end: endOfMonth(subDays(today, today.getDate())),
      },
      {
        label: "Current Quarter",
        start: thisQuarter.start,
        end: thisQuarter.end,
      },
      { label: "Last Quarter", start: lastQuarter.start, end: lastQuarter.end },
      // { label: "This Year", start: startOfYear(today), end: endOfYear(today) },
      // {
      //   label: "Last Year",
      //   start: startOfYear(subDays(today, 365)),
      //   end: endOfYear(subDays(today, 365)),
      // },
      { label: "Financial Year", start: thisFY.start, end: thisFY.end },
      { label: "Last Financial Year", start: lastFY.start, end: lastFY.end },
    ];

    const handleMouseOver = (part) => {
      setHighlightedPart(null);
    };

    const handleMouseLeave = () => {
      setHighlightedPart(null);
    };

    const handleWheel = (event, part) => {
      event.preventDefault();
      setSelectedRange(null);
      if (highlightedPart === "firstDay") {
        const newDate = new Date(date.from);
        const increment = event.deltaY > 0 ? -1 : 1;
        newDate.setDate(newDate.getDate() + increment);
        if (newDate <= date.to) {
          if (numberOfMonths === 2) {
            onDateSelect({ from: newDate, to: new Date(date.to) });
            setDate({ from: newDate, to: new Date(date.to) });
          } else {
            onDateSelect({ from: newDate, to: newDate });
            setDate({ from: newDate, to: newDate });
          }
          setMonthFrom(newDate);
        } else if (newDate > date.to && numberOfMonths === 1) {
          onDateSelect({ from: newDate, to: newDate });
          setDate({ from: newDate, to: newDate });
          setMonthFrom(newDate);
        }
      } else if (highlightedPart === "firstMonth") {
        const currentMonth = monthFrom ? monthFrom.getMonth() : 0;
        const newMonthIndex = currentMonth + (event.deltaY > 0 ? -1 : 1);
        handleMonthChange(newMonthIndex, "from");
      } else if (highlightedPart === "firstYear" && yearFrom !== undefined) {
        const newYear = yearFrom + (event.deltaY > 0 ? -1 : 1);
        handleYearChange(newYear, "from");
      } else if (highlightedPart === "secondDay") {
        const newDate = new Date(date.to);
        const increment = event.deltaY > 0 ? -1 : 1;
        newDate.setDate(newDate.getDate() + increment);
        if (newDate >= date.from) {
          onDateSelect({ from: new Date(date.from), to: newDate });
          setDate({ from: new Date(date.from), to: newDate });
          setMonthTo(newDate);
        }
      } else if (highlightedPart === "secondMonth") {
        const currentMonth = monthTo ? monthTo.getMonth() : 0;
        const newMonthIndex = currentMonth + (event.deltaY > 0 ? -1 : 1);
        handleMonthChange(newMonthIndex, "to");
      } else if (highlightedPart === "secondYear" && yearTo !== undefined) {
        const newYear = yearTo + (event.deltaY > 0 ? -1 : 1);
        handleYearChange(newYear, "to");
      }
    };

    React.useEffect(() => {
      const firstDayElement = document.getElementById(`firstDay-${id}`);
      const firstMonthElement = document.getElementById(`firstMonth-${id}`);
      const firstYearElement = document.getElementById(`firstYear-${id}`);
      const secondDayElement = document.getElementById(`secondDay-${id}`);
      const secondMonthElement = document.getElementById(`secondMonth-${id}`);
      const secondYearElement = document.getElementById(`secondYear-${id}`);

      const elements = [
        firstDayElement,
        firstMonthElement,
        firstYearElement,
        secondDayElement,
        secondMonthElement,
        secondYearElement,
      ];

      const addPassiveEventListener = (element) => {
        if (element) {
          element.addEventListener("wheel", handleWheel, {
            passive: false,
          });
        }
      };

      elements.forEach(addPassiveEventListener);

      return () => {
        elements.forEach((element) => {
          if (element) {
            element.removeEventListener("wheel", handleWheel);
          }
        });
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [highlightedPart, date]);

    const formatWithTz = (date, fmt) => formatInTimeZone(date, timeZone, fmt);

    return (
      <>
        <style>
          {`
            .date-part {
              touch-action: none;
            }
          `}
        </style>
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              id="date"
              ref={ref}
              {...props}
              className={cn(
                "w-auto",
                multiSelectVariants({ variant, className })
              )}
              onClick={handleTogglePopover}
              suppressHydrationWarning
            >
              <div
                className={
                  ddmmyyyyFormat
                    ? "flex flex-wrap justify-start"
                    : "flex items-center justify-center"
                }
              >
                {!ddmmyyyyFormat && <CalendarIcon className="mr-2 h-4 w-4" />}
                <span
                  className={
                    ddmmyyyyFormat ? "flex flex-wrap items-center" : ""
                  }
                >
                  {ddmmyyyyFormat ? (
                    date?.from ? (
                      date.to ? (
                        <>
                          <span
                            id={`firstDay-${id}`}
                            className={cn(
                              "date-part",
                              highlightedPart === "firstDay" && "font-bold"
                            )}
                            onMouseOver={() => handleMouseOver("firstDay")}
                            onMouseLeave={handleMouseLeave}
                          >
                            {formatWithTz(date.from, "dd")}
                          </span>
                          {"/"}
                          <span
                            id={`firstMonth-${id}`}
                            className={cn(
                              "date-part",
                              highlightedPart === "firstMonth" && "font-bold"
                            )}
                            onMouseOver={() => handleMouseOver("firstMonth")}
                            onMouseLeave={handleMouseLeave}
                          >
                            {formatWithTz(date.from, "MM")}
                          </span>
                          {"/"}
                          <span
                            id={`firstYear-${id}`}
                            className={cn(
                              "date-part",
                              highlightedPart === "firstYear" && "font-bold"
                            )}
                            onMouseOver={() => handleMouseOver("firstYear")}
                            onMouseLeave={handleMouseLeave}
                          >
                            {formatWithTz(date.from, "y")}
                          </span>
                          {numberOfMonths === 2 && (
                            <>
                              {" ~ "}
                              <span
                                id={`secondDay-${id}`}
                                className={cn(
                                  "date-part",
                                  highlightedPart === "secondDay" && "font-bold"
                                )}
                                onMouseOver={() => handleMouseOver("secondDay")}
                                onMouseLeave={handleMouseLeave}
                              >
                                {formatWithTz(date.to, "dd")}
                              </span>
                              {"/"}
                              <span
                                id={`secondMonth-${id}`}
                                className={cn(
                                  "date-part",
                                  highlightedPart === "secondMonth" &&
                                    "font-bold"
                                )}
                                onMouseOver={() =>
                                  handleMouseOver("secondMonth")
                                }
                                onMouseLeave={handleMouseLeave}
                              >
                                {formatWithTz(date.to, "MM")}
                              </span>
                              {"/"}
                              <span
                                id={`secondYear-${id}`}
                                className={cn(
                                  "date-part",
                                  highlightedPart === "secondYear" &&
                                    "font-bold"
                                )}
                                onMouseOver={() =>
                                  handleMouseOver("secondYear")
                                }
                                onMouseLeave={handleMouseLeave}
                              >
                                {formatWithTz(date.to, "y")}
                              </span>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <span
                            id="day"
                            className={cn(
                              "date-part",
                              highlightedPart === "day" && "underline font-bold"
                            )}
                            onMouseOver={() => handleMouseOver("day")}
                            onMouseLeave={handleMouseLeave}
                          >
                            {formatWithTz(date.from, "dd")}
                          </span>
                          {"/"}
                          <span
                            id="month"
                            className={cn(
                              "date-part",
                              highlightedPart === "month" &&
                                "underline font-bold"
                            )}
                            onMouseOver={() => handleMouseOver("month")}
                            onMouseLeave={handleMouseLeave}
                          >
                            {formatWithTz(date.from, "MM")}
                          </span>
                          {"/"}
                          <span
                            id="year"
                            className={cn(
                              "date-part",
                              highlightedPart === "year" &&
                                "underline font-bold"
                            )}
                            onMouseOver={() => handleMouseOver("year")}
                            onMouseLeave={handleMouseLeave}
                          >
                            {formatWithTz(date.from, "y")}
                          </span>
                        </>
                      )
                    ) : (
                      <span>Select Date</span>
                    )
                  ) : date?.from ? (
                    date.to ? (
                      <>
                        <span
                          id={`firstDay-${id}`}
                          className={cn(
                            "date-part",
                            highlightedPart === "firstDay" && "font-bold"
                          )}
                          onMouseOver={() => handleMouseOver("firstDay")}
                          onMouseLeave={handleMouseLeave}
                        >
                          {formatWithTz(date.from, "dd")}
                        </span>{" "}
                        <span
                          id={`firstMonth-${id}`}
                          className={cn(
                            "date-part",
                            highlightedPart === "firstMonth" && "font-bold"
                          )}
                          onMouseOver={() => handleMouseOver("firstMonth")}
                          onMouseLeave={handleMouseLeave}
                        >
                          {formatWithTz(date.from, "LLL")}
                        </span>
                        ,{" "}
                        <span
                          id={`firstYear-${id}`}
                          className={cn(
                            "date-part",
                            highlightedPart === "firstYear" && "font-bold"
                          )}
                          onMouseOver={() => handleMouseOver("firstYear")}
                          onMouseLeave={handleMouseLeave}
                        >
                          {formatWithTz(date.from, "y")}
                        </span>
                        {numberOfMonths === 2 && (
                          <>
                            {" - "}
                            <span
                              id={`secondDay-${id}`}
                              className={cn(
                                "date-part",
                                highlightedPart === "secondDay" && "font-bold"
                              )}
                              onMouseOver={() => handleMouseOver("secondDay")}
                              onMouseLeave={handleMouseLeave}
                            >
                              {formatWithTz(date.to, "dd")}
                            </span>{" "}
                            <span
                              id={`secondMonth-${id}`}
                              className={cn(
                                "date-part",
                                highlightedPart === "secondMonth" && "font-bold"
                              )}
                              onMouseOver={() => handleMouseOver("secondMonth")}
                              onMouseLeave={handleMouseLeave}
                            >
                              {formatWithTz(date.to, "LLL")}
                            </span>
                            ,{" "}
                            <span
                              id={`secondYear-${id}`}
                              className={cn(
                                "date-part",
                                highlightedPart === "secondYear" && "font-bold"
                              )}
                              onMouseOver={() => handleMouseOver("secondYear")}
                              onMouseLeave={handleMouseLeave}
                            >
                              {formatWithTz(date.to, "y")}
                            </span>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <span
                          id="day"
                          className={cn(
                            "date-part",
                            highlightedPart === "day" && "underline font-bold"
                          )}
                          onMouseOver={() => handleMouseOver("day")}
                          onMouseLeave={handleMouseLeave}
                        >
                          {formatWithTz(date.from, "dd")}
                        </span>{" "}
                        <span
                          id="month"
                          className={cn(
                            "date-part",
                            highlightedPart === "month" && "underline font-bold"
                          )}
                          onMouseOver={() => handleMouseOver("month")}
                          onMouseLeave={handleMouseLeave}
                        >
                          {formatWithTz(date.from, "LLL")}
                        </span>
                        ,{" "}
                        <span
                          id="year"
                          className={cn(
                            "date-part",
                            highlightedPart === "year" && "underline font-bold"
                          )}
                          onMouseOver={() => handleMouseOver("year")}
                          onMouseLeave={handleMouseLeave}
                        >
                          {formatWithTz(date.from, "y")}
                        </span>
                      </>
                    )
                  ) : (
                    <span>Select Date</span>
                  )}
                  {onClear && date?.from && (
                    <span>
                      <XIcon onClick={onClear} className="h-4 w-4" />
                    </span>
                  )}
                </span>
              </div>
            </Button>
          </PopoverTrigger>
          {isPopoverOpen && (
            <PopoverContent
              className="w-auto"
              align={align}
              avoidCollisions={false}
              onInteractOutside={handleClose}
              onEscapeKeyDown={handleClose}
              style={{
                maxHeight: "var(--radix-popover-content-available-height)",
                overflowY: "auto",
              }}
            >
              <div className="flex">
                {numberOfMonths === 2 && (
                  <div className="flex flex-col gap-1 pr-4 text-left border-r border-foreground/10">
                    {dateRanges.map(({ label, start, end }) => (
                      <Button
                        key={label}
                        variant="ghost"
                        size="sm"
                        className={cn(
                          "justify-start hover:bg-primary/90 hover:text-background",
                          selectedRange === label &&
                            "bg-primary text-background hover:bg-primary/90 hover:text-background"
                        )}
                        onClick={() => {
                          selectDateRange(start, end, label);
                          setMonthFrom(start);
                          setYearFrom(start.getFullYear());
                          setMonthTo(end);
                          setYearTo(end.getFullYear());
                        }}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                )}
                <div className="flex flex-col">
                  <div className="hidden sm:flex items-center gap-4">
                    <div className="flex gap-2 ml-3">
                      <Select
                        onValueChange={(value) => {
                          handleMonthChange(months.indexOf(value), "from");
                          setSelectedRange(null);
                        }}
                        value={
                          monthFrom ? months[monthFrom.getMonth()] : undefined
                        }
                      >
                        <SelectTrigger className="hidden sm:flex w-[122px] focus:ring-0 focus:ring-offset-0 font-medium hover:bg-accent hover:text-accent-foreground">
                          <SelectValue placeholder="Month" />
                        </SelectTrigger>
                        <SelectContent>
                          {months.map((month, idx) => (
                            <SelectItem key={idx} value={month}>
                              {month}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        onValueChange={(value) => {
                          handleYearChange(Number(value), "from");
                          setSelectedRange(null);
                        }}
                        value={yearFrom ? yearFrom.toString() : undefined}
                      >
                        <SelectTrigger className="hidden sm:flex w-[122px] focus:ring-0 focus:ring-offset-0 font-medium hover:bg-accent hover:text-accent-foreground">
                          <SelectValue placeholder="Year" />
                        </SelectTrigger>
                        <SelectContent>
                          {years.map((year, idx) => (
                            <SelectItem key={idx} value={year.toString()}>
                              {year}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {numberOfMonths === 2 && (
                      <div className="flex gap-2">
                        <Select
                          onValueChange={(value) => {
                            handleMonthChange(months.indexOf(value), "to");
                            setSelectedRange(null);
                          }}
                          value={
                            monthTo ? months[monthTo.getMonth()] : undefined
                          }
                        >
                          <SelectTrigger className="hidden sm:flex w-[122px] focus:ring-0 focus:ring-offset-0 font-medium hover:bg-accent hover:text-accent-foreground">
                            <SelectValue placeholder="Month" />
                          </SelectTrigger>
                          <SelectContent>
                            {months.map((month, idx) => (
                              <SelectItem key={idx} value={month}>
                                {month}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          onValueChange={(value) => {
                            handleYearChange(Number(value), "to");
                            setSelectedRange(null);
                          }}
                          value={yearTo ? yearTo.toString() : undefined}
                        >
                          <SelectTrigger className="hidden sm:flex w-[122px] focus:ring-0 focus:ring-offset-0 font-medium hover:bg-accent hover:text-accent-foreground">
                            <SelectValue placeholder="Year" />
                          </SelectTrigger>
                          <SelectContent>
                            {years.map((year, idx) => (
                              <SelectItem key={idx} value={year.toString()}>
                                {year}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  <div className="hidden sm:flex justify-center">
                    <Calendar
                      mode="range"
                      defaultMonth={monthFrom}
                      month={monthFrom}
                      onMonthChange={setMonthFrom}
                      selected={date}
                      onSelect={handleDateSelect}
                      numberOfMonths={numberOfMonths}
                      showOutsideDays={false}
                      className={className}
                      disabled={disabledDate}
                    />
                  </div>
                </div>
              </div>
            </PopoverContent>
          )}
        </Popover>
      </>
    );
  }
);

CalendarDatePicker.displayName = "CalendarDatePicker";
